import { useMemo } from 'react';
import { format } from 'date-fns';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import styled from 'styled-components';

import {
  MobileSubtitle,
  SectionCard,
  SectionHeader,
  SectionTitle,
  TitleColumn,
  TitleGroup,
} from 'components/insights/careTransitions/shared/tableStyles';
import { Metrics } from 'components/insights/constants';
import { formatWholeNumber } from 'components/insights/formatting';
import CircularProgress from 'components/shared/CircularProgress';
import DateRangeBadge from 'components/shared/DateRangeBadge';
import DesktopOnly from 'components/shared/responsiveness/DesktopOnly';
import MobileOnly from 'components/shared/responsiveness/MobileOnly';
import { END_DATE, START_DATE } from 'constants/filterKeysConstants';
import { useBusinessDevelopmentStore } from 'stores/businessDevelopmentStore';
import { colors } from 'styles/theme/colors';

import { SharedAnalytics } from '../BusinessDevelopmentDashboard';
import { TABLE_PIVOT_OPTIONS } from '../constants';

type Props = {
  data: SharedAnalytics;
  chartMetric: Metrics;
};

export default function ReferralSourceBreakoutChart({ data, chartMetric }: Props) {
  const viewBy = useBusinessDevelopmentStore((state) => state.viewBy);
  const tablePivot = useBusinessDevelopmentStore((state) => state.tablePivot);
  const groupTypeName = useBusinessDevelopmentStore((state) => state.selectedGroupType!.displayName);
  const startDate = useBusinessDevelopmentStore((state) => state.filters[START_DATE]) as Date;
  const endDate = useBusinessDevelopmentStore((state) => state.filters[END_DATE]) as Date;

  const { loading, rollups } = data;
  const viewByLabel = viewBy === 'payerSource' ? 'Payer Source' : 'Referral Source';
  const pivotLabel = TABLE_PIVOT_OPTIONS.find((o) => o.value === tablePivot)?.label ?? 'Patient Volume';
  const seriesName = tablePivot === 'referrals' ? 'Total Referrals' : `${groupTypeName} ${pivotLabel}`;

  const chartOptions = useMemo((): Highcharts.Options => {
    const entityGrouping = rollups.groupings[1];
    const entityNames = entityGrouping?.dimensions.getCurrentDimensionValuesByIndex(0) ?? [];
    const values = entityGrouping?.metrics.getCurrentSeries(chartMetric) ?? [];

    return {
      chart: { type: 'column', style: { fontFamily: 'Lato' }, height: 280 },
      accessibility: { enabled: false },
      credits: { enabled: false },
      title: { text: undefined },
      legend: {
        align: 'center',
        verticalAlign: 'bottom',
        symbolRadius: 6,
        itemStyle: { fontSize: '12px', fontWeight: 'normal', color: colors.black75 },
      },
      xAxis: {
        categories: entityNames,
        lineColor: colors.black10,
        tickLength: 0,
        labels: {
          style: { fontSize: '11px', color: colors.black75 },
          rotation: entityNames.length > 8 ? -45 : 0,
        },
      },
      yAxis: {
        title: { text: undefined },
        gridLineColor: colors.black10,
        labels: { style: { fontSize: '11px', color: colors.black50 } },
      },
      tooltip: {
        backgroundColor: colors.chartTooltipBackground,
        style: { color: colors.white },
        padding: 12,
        useHTML: true,
        formatter: function () {
          return `
            <b style="font-size: 12px; font-weight: bold;">${this.x}</b>
            <br />
            <div style="padding-top: 4px;">
              <span style="font-size: 12px;">${formatWholeNumber(this.y ?? 0)}</span>
            </div>`;
        },
      },
      plotOptions: {
        column: {
          borderRadius: { radius: 3, scope: 'point' as const, where: 'end' as const },
          pointWidth: 40,
        },
        series: { animation: false },
      },
      series: [
        {
          type: 'column',
          name: seriesName,
          data: values,
          color: viewBy === 'payerSource' ? colors.accentGreen75 : colors.primaryBlue,
        },
      ],
    };
  }, [rollups, chartMetric, seriesName, viewBy]);

  return (
    <SectionCard>
      <SectionHeader>
        <TitleGroup>
          <TitleColumn>
            <SectionTitle>{viewByLabel} Breakout</SectionTitle>
            <MobileOnly>
              <MobileSubtitle>
                {format(startDate, 'MM/dd/yyyy')} - {format(endDate, 'MM/dd/yyyy')}
              </MobileSubtitle>
            </MobileOnly>
          </TitleColumn>
          <DesktopOnly>
            <DateRangeBadge startDate={startDate} endDate={endDate} />
          </DesktopOnly>
        </TitleGroup>
      </SectionHeader>
      <ChartBody>
        {loading ? (
          <SpinnerContainer>
            <CircularProgress color={colors.primaryBlue} />
          </SpinnerContainer>
        ) : (
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        )}
      </ChartBody>
    </SectionCard>
  );
}

const ChartBody = styled.div`
  padding: 0 24px 24px;
  min-height: 280px;
`;

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 280px;
`;
